import React, { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Clock, ListChecks, Target, FileText, CheckCircle2, AlertCircle, Truck, Paperclip, ClipboardCheck, Ban, Activity } from 'lucide-react';

/* Day 9 — shared by Mobile App (/mobile/shifts/:id) and Worker Web Portal (/staff/shift/:id).
   Both read GET /mobile/shifts/{id} → data.workerContext (same backend records). */

const REASON_LABELS = {
  PARTICIPANT_LEFT_EARLY: 'Participant left early', PARTICIPANT_ARRIVED_LATE: 'Participant arrived late',
  PARTICIPANT_UNWELL: 'Participant unwell', PARTICIPANT_NOT_HOME: 'Participant not home',
  PARTICIPANT_DECLINED: 'Participant declined support', FAMILY_CANCELLED_AT_DOOR: 'Family cancelled at the door', OTHER: 'Other',
};
const EVIDENCE_LABELS = { NON_FACE_TO_FACE: 'Non face-to-face support', REPORT_WRITING: 'Report writing' };
const errMsg = (e, fallback) => e?.response?.data?.detail?.message || e?.response?.data?.detail?.error?.message || fallback;
const toLocal = (date, hhmm) => `${date}T${(hhmm || '00:00').slice(0, 5)}:00`;

function PromptList({ prompts, onAction }) {
  if (!prompts?.length) return null;
  return (
    <div className="space-y-2" data-testid="evidence-prompts">
      {prompts.map((p, i) => (
        <div key={`${p.code}-${p.participantId || 'shift'}-${i}`} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800" data-testid={`evidence-prompt-${p.code}`}>
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">{p.label}</span>
          {p.action && onAction && (
            <button className="text-xs font-semibold underline underline-offset-2" onClick={() => onAction(p)} data-testid={`evidence-prompt-action-${p.code}`}>Do it</button>
          )}
        </div>
      ))}
    </div>
  );
}

function AttendanceForm({ shiftId, shiftDate, participant, reasons, onDone, onCancel }) {
  const [status, setStatus] = useState('ATTENDED');
  const [start, setStart] = useState(participant.times?.start_time || '');
  const [end, setEnd] = useState(participant.times?.end_time || '');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { participantId: participant.id, status, reasonCode: reason || null, notes: notes || null };
      if (status !== 'NO_SHOW') { body.actualStart = toLocal(shiftDate, start); body.actualEnd = toLocal(shiftDate, end); }
      const r = await api.post(`/mobile/shifts/${shiftId}/attendance`, body);
      toast.success(r.data?.cancellationReviewRequested ? 'Recorded — sent to your coordinator for review' : 'Attendance recorded');
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not record attendance')); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-lg border p-3 bg-muted/30" data-testid={`attendance-form-${participant.id}`}>
      <div className="grid grid-cols-3 gap-1">
        {[['ATTENDED', 'Attended'], ['PARTIAL', 'Partial'], ['NO_SHOW', 'Did not attend']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setStatus(v)} className={`rounded-md border px-2 py-1.5 text-xs font-medium ${status === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-background'}`} data-testid={`attendance-status-${v}`}>{l}</button>
        ))}
      </div>
      {status !== 'NO_SHOW' && (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Actual start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="attendance-actual-start" /></div>
          <div><Label className="text-xs">Actual end</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="attendance-actual-end" /></div>
        </div>
      )}
      {status !== 'ATTENDED' && (
        <>
          <div>
            <Label className="text-xs">Reason</Label>
            <select className="w-full rounded-md border bg-background px-2 py-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="attendance-reason">
              <option value="">Select a reason…</option>
              {reasons.map((r) => <option key={r} value={r}>{REASON_LABELS[r] || r}</option>)}
            </select>
          </div>
          <Textarea rows={2} placeholder="What happened? (required)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="attendance-notes" />
          <p className="text-[11px] text-muted-foreground">Your coordinator reviews partial delivery / did-not-attend. You do not decide billing or cancellation charges.</p>
        </>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy} data-testid="attendance-submit">{busy ? 'Saving…' : 'Save'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="attendance-cancel">Cancel</Button>
      </div>
    </div>
  );
}

function EvidenceForm({ shiftId, participants, types, presetParticipantId, onDone, onCancel }) {
  const [type, setType] = useState(types[0]);
  const [pid, setPid] = useState(presetParticipantId || participants[0]?.id || '');
  const [hours, setHours] = useState('0.5');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/mobile/shifts/${shiftId}/evidence`, { evidenceType: type, participantId: pid, hours: parseFloat(hours), notes });
      toast.success('Submitted for verification');
      onDone();
    } catch (e) { toast.error(errMsg(e, 'Could not submit evidence')); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-lg border p-3 bg-muted/30" data-testid="evidence-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Type</Label>
          <select className="w-full rounded-md border bg-background px-2 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="evidence-type">
            {types.map((t) => <option key={t} value={t}>{EVIDENCE_LABELS[t] || t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Participant</Label>
          <select className="w-full rounded-md border bg-background px-2 py-2 text-sm" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="evidence-participant">
            {participants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div><Label className="text-xs">Hours</Label><Input type="number" step="0.25" min="0.25" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="evidence-hours" /></div>
      <Textarea rows={2} placeholder="What did you do? (required)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="evidence-notes" />
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={busy} data-testid="evidence-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="evidence-cancel">Cancel</Button>
      </div>
    </div>
  );
}

function ParticipantCard({ p, shiftId, shiftDate, ctx, onRefresh, onAddNote, onOpenProfile, onOpenCharts }) {
  const [showAttendance, setShowAttendance] = useState(false);
  const att = p.attendance;
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid={`worker-participant-${p.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <button className="font-medium text-left text-primary hover:underline underline-offset-2" onClick={() => onOpenProfile?.(p.id)} data-testid={`worker-participant-name-${p.id}`}>
            {p.name}{p.preferredName ? ` (${p.preferredName})` : ''}
          </button>
          <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`worker-participant-times-${p.id}`}>
            <Clock className="h-3 w-3" /> {p.times?.start_time} – {p.times?.end_time}
            {p.times?.participant_specific && <Badge variant="outline" className="ml-1 text-[10px] py-0">participant time</Badge>}
          </p>
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {p.isPrimary && ctx.isMultiParticipant && <Badge variant="secondary" className="text-[10px]">Primary</Badge>}
          {p.careFlags?.map((f) => <Badge key={f} variant="outline" className="text-[10px] bg-red-500/5 text-red-600 border-red-500/20">{f}</Badge>)}
        </div>
      </div>

      {p.tasks?.length > 0 && (
        <div data-testid={`worker-participant-tasks-${p.id}`}>
          <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1"><ListChecks className="h-3 w-3" /> TASKS</p>
          <ul className="text-sm space-y-0.5">{p.tasks.map((t) => <li key={t.id} className="flex items-center gap-2" data-testid="worker-task-item"><span className={`h-1.5 w-1.5 rounded-full ${t.priority === 'HIGH' ? 'bg-red-500' : 'bg-primary'}`} />{t.title}{t.dueDate && <span className="text-xs text-muted-foreground">due {t.dueDate}</span>}</li>)}</ul>
        </div>
      )}
      {p.goals?.length > 0 && (
        <div data-testid={`worker-participant-goals-${p.id}`}>
          <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1"><Target className="h-3 w-3" /> GOALS / OUTCOMES</p>
          <ul className="text-sm space-y-0.5">{p.goals.map((g) => <li key={g.id} data-testid="worker-goal-item"><span className="font-medium">{g.title}</span>{g.outcome && <span className="text-xs text-muted-foreground"> — {g.outcome}</span>}</li>)}</ul>
        </div>
      )}
      <div className="flex items-center gap-2 text-xs" data-testid={`worker-participant-notes-${p.id}`}>
        <FileText className="h-3 w-3 text-muted-foreground" />
        <span className="text-muted-foreground">{p.notes?.length ? `${p.notes.length} note${p.notes.length > 1 ? 's' : ''} this shift` : 'No note yet'}</span>
        {onAddNote && !ctx.capabilities?.readOnly && (
          <button className="ml-auto text-primary font-semibold underline-offset-2 hover:underline" onClick={() => onAddNote(p.id)} data-testid={`worker-add-note-${p.id}`}>+ Note</button>
        )}
      </div>
      {onOpenCharts && !ctx.capabilities?.readOnly && (
        <div className="flex items-center gap-2 text-xs" data-testid={`worker-participant-charts-${p.id}`}>
          <Activity className="h-3 w-3 text-muted-foreground" />
          <span className="text-muted-foreground">Clinical charts</span>
          <button className="ml-auto text-primary font-semibold underline-offset-2 hover:underline" onClick={() => onOpenCharts(p.id)} data-testid={`worker-open-charts-${p.id}`}>+ Chart</button>
        </div>
      )}
      <div className="flex items-center gap-2 text-xs" data-testid={`worker-participant-attendance-${p.id}`}>
        {att ? (
          <>
            {att.status === 'ATTENDED' ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <Ban className="h-3.5 w-3.5 text-amber-600" />}
            <span data-testid={`worker-attendance-status-${p.id}`}>{att.status === 'ATTENDED' ? 'Attended' : att.status === 'PARTIAL' ? 'Partial delivery' : 'Did not attend'}{att.reasonCode ? ` · ${REASON_LABELS[att.reasonCode] || att.reasonCode}` : ''}</span>
            <Badge variant="outline" className="text-[10px] py-0">{att.approved ? 'Reviewed' : 'Awaiting review'}</Badge>
          </>
        ) : (
          <span className="text-muted-foreground">Attendance not recorded</span>
        )}
        {ctx.capabilities?.canRecordAttendance && !att?.approved && (
          <button className="ml-auto text-primary font-semibold underline-offset-2 hover:underline" onClick={() => setShowAttendance((v) => !v)} data-testid={`worker-record-attendance-${p.id}`}>{att ? 'Update' : 'Record'}</button>
        )}
      </div>
      {showAttendance && <AttendanceForm shiftId={shiftId} shiftDate={shiftDate} participant={p} reasons={ctx.attendanceReasons || []} onDone={() => { setShowAttendance(false); onRefresh(); }} onCancel={() => setShowAttendance(false)} />}
    </div>
  );
}

export function WorkerShiftParticipantsPanel({ shiftId, shiftDate, context, onRefresh, onAddNote, onOpenProfile, onOpenCharts, onPromptAction, compact = false }) {
  const [showEvidence, setShowEvidence] = useState(null);
  if (!context) return null;
  const ctx = context;
  const handlePrompt = (p) => {
    if (p.action === 'EVIDENCE') { setShowEvidence(p.participantId || true); return; }
    onPromptAction?.(p);
  };
  return (
    <Card data-testid="worker-shift-panel">
      <CardContent className={compact ? 'p-4 space-y-3' : 'p-5 space-y-4'}>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold">{ctx.isMultiParticipant ? `Participants (${ctx.participantCount})` : 'Participant'}</p>
          {ctx.serviceFamily?.name && <Badge variant="secondary" className="ml-auto text-xs" data-testid="worker-service-family">{ctx.serviceFamily.name}</Badge>}
        </div>

        {ctx.visibilityRestriction && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800" data-testid="worker-visibility-restriction-notice">
            You do not have permission to view every participant on this shared-support shift. Shared-support participant access is currently restricted — contact your coordinator if you need more detail.
          </div>
        )}

        {ctx.cancellation && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700" data-testid="worker-cancellation-summary">
            <p className="font-medium">Shift cancelled{ctx.cancellation.reasonCode ? ` · ${String(ctx.cancellation.reasonCode).replace(/_/g, ' ').toLowerCase()}` : ''}</p>
            {ctx.cancellation.staffPayable != null && <p className="text-xs">{ctx.cancellation.staffPayable ? 'You will be paid for this shift.' : 'This shift is not payable.'} Participant billing is decided separately by your coordinator.</p>}
          </div>
        )}

        <PromptList prompts={ctx.evidencePrompts} onAction={handlePrompt} />

        <div className="space-y-2" data-testid="worker-participants-list">
          {ctx.participants.map((p) => (
            <ParticipantCard key={p.id} p={p} shiftId={shiftId} shiftDate={shiftDate} ctx={ctx} onRefresh={onRefresh} onAddNote={onAddNote} onOpenProfile={onOpenProfile} onOpenCharts={onOpenCharts} />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border p-2" data-testid="worker-vehicle-logs">
            <p className="font-semibold flex items-center gap-1 text-muted-foreground"><Truck className="h-3 w-3" /> VEHICLE LOG</p>
            {ctx.vehicleLogs?.length ? ctx.vehicleLogs.map((v) => <p key={v.id} data-testid="worker-vehicle-log-item">{v.distanceKm} km · {v.status?.replace(/_/g, ' ').toLowerCase()}</p>) : <p className="text-muted-foreground">No trips logged</p>}
            {onPromptAction && !ctx.capabilities?.readOnly && <button className="text-primary font-semibold mt-1" onClick={() => onPromptAction({ action: 'TRANSPORT' })} data-testid="worker-add-vehicle-log">+ Log trip</button>}
          </div>
          <div className="rounded-lg border p-2" data-testid="worker-attachments">
            <p className="font-semibold flex items-center gap-1 text-muted-foreground"><Paperclip className="h-3 w-3" /> ATTACHMENTS</p>
            {ctx.attachments?.length ? ctx.attachments.map((a) => <p key={a.id} className="truncate" data-testid="worker-attachment-item">{a.filename || a.entityType}</p>) : <p className="text-muted-foreground">None</p>}
            {onPromptAction && <button className="text-primary font-semibold mt-1" onClick={() => onPromptAction({ action: 'ATTACHMENT' })} data-testid="worker-add-attachment">+ Add</button>}
          </div>
        </div>

        <div className="rounded-lg border p-2 text-xs" data-testid="worker-evidence">
          <div className="flex items-center gap-1">
            <ClipboardCheck className="h-3 w-3 text-muted-foreground" />
            <p className="font-semibold text-muted-foreground">OTHER SUPPORT (non face-to-face, report writing)</p>
            {ctx.capabilities?.canSubmitEvidence && <button className="ml-auto text-primary font-semibold" onClick={() => setShowEvidence((v) => (v ? null : true))} data-testid="worker-add-evidence">+ Add</button>}
          </div>
          {ctx.evidence?.length ? ctx.evidence.map((e) => <p key={e.id} data-testid="worker-evidence-item">{EVIDENCE_LABELS[e.evidenceType] || e.evidenceType} · {e.hours} h · <span className="text-muted-foreground">{e.status === 'VERIFIED' ? 'Verified' : e.status === 'REJECTED' ? 'Not accepted' : 'Awaiting verification'}</span></p>) : <p className="text-muted-foreground">Nothing submitted</p>}
          {showEvidence && <EvidenceForm shiftId={shiftId} participants={ctx.participants} types={ctx.evidenceTypes || []} presetParticipantId={typeof showEvidence === 'string' ? showEvidence : null} onDone={() => { setShowEvidence(null); onRefresh(); }} onCancel={() => setShowEvidence(null)} />}
        </div>
      </CardContent>
    </Card>
  );
}

export default WorkerShiftParticipantsPanel;
